import { useCallback, useEffect, useRef } from "react";
import { useTripleClick } from "./useTripleClick";
import { useTripleTouch } from "./useTripleTouch";

const COOLDOWN_DURATION = 2000;

export function usePinTrigger({
  onTrigger,
  excludeSelectors = [],
  clickDelay = 500,
  holdDuration = 5000,
}: {
  onTrigger?: () => void;
  excludeSelectors?: string[];
  clickDelay?: number;
  holdDuration?: number;
}) {
  const isTriggered = useRef(false);
  const triggerCooldown = useRef(0);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchDevice = useRef(false);
  const lastTouchAt = useRef(0);
  const onTriggerRef = useRef(onTrigger);
  const excludeSelectorsRef = useRef(excludeSelectors);
  onTriggerRef.current = onTrigger;
  excludeSelectorsRef.current = excludeSelectors;

  const safeTrigger = useCallback(() => {
    const now = Date.now();
    if (now - triggerCooldown.current < COOLDOWN_DURATION) return;
    if (isTriggered.current) return;

    isTriggered.current = true;
    triggerCooldown.current = now;
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      isTriggered.current = false;
    }, 1000);
    onTriggerRef.current?.();
  }, []);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const { handleClick, reset: resetClick } = useTripleClick({
    delay: clickDelay,
    onTripleClick: safeTrigger,
  });

  const {
    handleTouchStart,
    handleTouchEnd,
    handleTouchMove,
    reset: resetTouch,
  } = useTripleTouch({
    onTripleTouch: safeTrigger,
    holdDuration,
  });

  useEffect(() => {
    isTouchDevice.current =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);

  useEffect(() => {
    const isExcluded = (event: Event) => {
      const target = event.target as Element | null;
      return excludeSelectorsRef.current.some(
        (selector) => target?.matches?.(selector) || target?.closest?.(selector)
      );
    };

    const handleGlobalClick = (event: MouseEvent) => {
      if (Date.now() - lastTouchAt.current < 700 || isExcluded(event)) return;
      handleClick(event);
    };

    const handleGlobalTouchStart = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        isTouchDevice.current = true;
        lastTouchAt.current = Date.now();
        if (event.touches.length >= 3 && event.cancelable) {
          event.preventDefault();
        }
        handleTouchStart(event);
      }
    };

    const handleGlobalTouchEnd = (event: TouchEvent) => {
      if (isTouchDevice.current) handleTouchEnd(event);
    };

    const handleGlobalTouchMove = (event: TouchEvent) => {
      if (isTouchDevice.current) handleTouchMove(event);
    };

    const touchOptions = { capture: true, passive: true };
    const touchStartOptions = { capture: true, passive: false };

    document.addEventListener("click", handleGlobalClick, true);
    document.addEventListener("touchstart", handleGlobalTouchStart, touchStartOptions);
    document.addEventListener("touchend", handleGlobalTouchEnd, touchOptions);
    document.addEventListener("touchcancel", handleGlobalTouchEnd, touchOptions);
    document.addEventListener("touchmove", handleGlobalTouchMove, touchOptions);

    return () => {
      document.removeEventListener("click", handleGlobalClick, true);
      document.removeEventListener("touchstart", handleGlobalTouchStart, true);
      document.removeEventListener("touchend", handleGlobalTouchEnd, true);
      document.removeEventListener("touchcancel", handleGlobalTouchEnd, true);
      document.removeEventListener("touchmove", handleGlobalTouchMove, true);
      resetClick();
      resetTouch();
    };
  }, [
    handleClick,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    resetClick,
    resetTouch,
  ]);
}
